/* @jsx h */
import { defineComponent, h, Fragment, computed, ref, watch } from "vue";
import { RouterLink } from "vue-router";
import { useAuth } from "@app/vue/providers/Auth";
import { useShop } from "@app/vue/providers/index";
import { useNativeBridge } from "@app/vue/providers/NativeBridge";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import type { SessionSibling } from "@app/server/trpc/routers/session";
import { isPrinterOnline } from "@app/utils/index";
import { api, instrumentation } from "@app/data/index";
import { ThemeSwitcher, useLocalization } from "@lemmonhq/uikit";
import { getSupportedLanguagesForJurisdictions } from "@app/utils/supportedLanguages";
import { AccountMenu } from "./AccountMenu";
import { GroupQuickLinks } from "./GroupQuickLinks";
import { ReleaseInfo } from "./ReleaseInfo";
import { RestaurantSwitcherImage } from "./RestaurantSwitcherImage";

type PosQuickLinksProps = {
  onRestaurantInfoOpen?: Function;
};
const PosQuickLinks = defineComponent<PosQuickLinksProps>({
  name: "PosQuickLinks",
  props: ["onRestaurantInfoOpen"],
  setup(props) {
    const auth = useAuth();
    const shop = useShop();

    // Try to get native bridge context (may not be available in all environments)
    let nativeBridge: any;
    try {
      nativeBridge = useNativeBridge();
    } catch (e) {
      console.error(e);
      // Not in a NativeBridge context, that's ok
    }

    const handleLogout = async (e: MouseEvent) => {
      e.preventDefault();
      try {
        const restaurantId = auth.user.value?.restaurant_id;
        if (restaurantId) {
          localStorage.setItem("shopId", restaurantId.toString());
        }
        await api.destroySession();
        instrumentation.reset();
        shop.bc.postMessage({ type: "logout" });

        // Notify native app about logout if available
        if (nativeBridge) {
          await nativeBridge.logout();
        }

        window.location.href = "/";
      } catch (error) {
        console.error("Error during logout:", error);
        //        window.location.href = "/";
      }
    };

    const allPrintersOnline = computed(() => {
      const printerStatuses = shop.printerStatuses.timestamps;
      const printers = shop.getPrinters();
      return printers?.every((el) => isPrinterOnline(printerStatuses, el.id));
    });

    // POS persona switcher: other restaurants linked to the signed-in identity.
    // Separate from PIN-based staff switching. A successful switch performs a
    // full reload so Auth.tsx re-emits the new shop_id through NativeBridge.
    const trpc = useTrpc();
    const siblings = ref<SessionSibling[]>([]);
    const switchingUserId = ref<number | null>(null);
    const enteringGroupMode = ref(false);

    const loadSiblings = async () => {
      try {
        siblings.value = await trpc.session.siblings.query();
      } catch (error) {
        console.error("Failed to load linked restaurants:", error);
        siblings.value = [];
      }
    };

    watch(
      () => auth.user.value?.id,
      (userId) => {
        if (userId) {
          void loadSiblings();
        } else {
          siblings.value = [];
        }
      },
      { immediate: true },
    );

    const switchRestaurant = async (userId: number) => {
      if (switchingUserId.value !== null) return;
      switchingUserId.value = userId;
      try {
        await trpc.session.switchShop.mutate({ userId });
        // Full reload so the new POS cookie is picked up everywhere and
        // Auth.tsx re-emits shop_id through the native bridge.
        window.location.reload();
      } catch (error) {
        console.error("Failed to switch restaurant:", error);
        switchingUserId.value = null;
      }
    };

    const enterGroupMode = async () => {
      if (enteringGroupMode.value) return;
      enteringGroupMode.value = true;
      try {
        await trpc.session.enterGroupMode.mutate();
        window.location.assign("/group/dashboard");
      } catch (error) {
        console.error("Failed to enter Consolidated mode:", error);
        enteringGroupMode.value = false;
      }
    };

    const il8n = useLocalization();
    const { t } = il8n;
    const loadingLang = ref<string | null>(null);

    const handleSetLocale = async (langId: string) => {
      loadingLang.value = langId;
      await il8n.setLocale(langId);
      loadingLang.value = null;
    };
    const getAvailableLanguages = () => {
      return getSupportedLanguagesForJurisdictions([
        auth.user.value?.restaurant.jurisdiction,
      ]);
    };

    return () => {
      console.log("import.meta.env.VITE_ENV", import.meta.env.VITE_ENV);
      if (!auth.user.value) {
        return (
          <div class="flex gap-2 w-full justify-end items-center px-2">
            <RouterLink to="/login" class="btn btn-primary">
              {t("nav.quick_links.login")}
            </RouterLink>
            {/* /signup works everywhere; it is only advertised on staging. */}
            {import.meta.env.VITE_ENV === "staging" && (
              <RouterLink to="/signup" class="btn btn-ghost">
                {t("nav.quick_links.signup")}
              </RouterLink>
            )}
          </div>
        );
      }

      const user = auth.user.value;

      const content = (
        <Fragment>
          <div class="pr-0 z-[2147483647] flex justify-end items-center flex-1 gap-3">
            {user.role === "admin" ? (
              <div class="flex-1"></div>
            ) : (
              <RouterLink
                to={"/app/devices/"}
                class="hidden xl:flex items-center gap-1.5 px-0.5 py-1 rounded-full transition-colors duration-200 hover:bg-base-content/5"
                title={
                  allPrintersOnline.value
                    ? t("nav.quick_links.printers_online")
                    : t("nav.quick_links.printers_offline")
                }
              >
                <span
                  class={`material-symbols-outlined text-[16px] ${
                    allPrintersOnline.value ? "text-success" : "text-warning"
                  }`}
                >
                  print
                </span>
                <span
                  class={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                    allPrintersOnline.value
                      ? "bg-success"
                      : "bg-warning animate-pulse"
                  }`}
                />
              </RouterLink>
            )}

            {/* Divider */}

            <AccountMenu
              name={user.profile.name}
              contextName={user.restaurant.name}
              showHeaderDivider
              renderItems={() => (
                <Fragment>
                  {/* Printer status — shown here when it doesn't fit in the top bar */}
                  {user.role !== "admin" && (
                    <Fragment>
                      <li class="xl:hidden">
                        <RouterLink
                          to="/app/devices/"
                          class="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-base-content/70 hover:text-accent  transition-colors duration-150"
                        >
                          <span
                            class={`material-symbols-outlined text-[18px] ${
                              allPrintersOnline.value
                                ? "text-success"
                                : "text-warning"
                            }`}
                          >
                            print
                          </span>
                          {allPrintersOnline.value
                            ? t("nav.quick_links.printers_online")
                            : t("nav.quick_links.printers_offline")}
                        </RouterLink>
                      </li>
                      <div class="xl:hidden mx-2 my-1 h-px bg-white/6" />
                    </Fragment>
                  )}

                  {/* Restaurant QR */}
                  <li>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        props.onRestaurantInfoOpen?.();
                      }}
                      class="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-base-content/70 hover:text-accent  transition-colors duration-150"
                    >
                      <span class="material-symbols-outlined text-[18px]">
                        qr_code
                      </span>
                      Restaurant QR
                    </a>
                  </li>

                  <div class="mx-2 my-1 h-px bg-white/6" />

                  {/* Consolidated mode is available to group admins and to
                      employees who manage more than one restaurant. */}
                  {(auth.groupRole.value === "admin" ||
                    (auth.groupRole.value === "employee" &&
                      siblings.value.filter(
                        (sibling) => sibling.pos_role === "manager",
                      ).length > 1)) && (
                    <Fragment>
                      <li>
                        <a
                          href="/group/dashboard"
                          onClick={(event) => {
                            event.preventDefault();
                            void enterGroupMode();
                          }}
                          class="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-primary hover:bg-primary/10 transition-colors duration-150"
                        >
                          {enteringGroupMode.value ? (
                            <span class="loading loading-spinner loading-xs" />
                          ) : (
                            <span class="material-symbols-outlined text-[18px]">
                              domain
                            </span>
                          )}
                          {t("admin.nav.admin_mode")}
                        </a>
                      </li>
                      <div class="mx-2 my-1 h-px bg-white/6" />
                    </Fragment>
                  )}

                  {/* Restaurant switcher — other shops linked to this identity */}
                  {siblings.value.length > 1 && (
                    <Fragment>
                      <li class="pointer-events-none px-3 pt-1.5 pb-0.5">
                        <span class="text-[10px] text-base-content/30 uppercase tracking-widest font-semibold p-0 hover:bg-transparent">
                          {t("nav.quick_links.switch_restaurant")}
                        </span>
                      </li>
                      {siblings.value.map((sibling) => (
                        <li class="py-0.5" key={sibling.user_id}>
                          <a
                            onClick={(e) => {
                              e.preventDefault();
                              if (!sibling.is_current) {
                                void switchRestaurant(sibling.user_id);
                              }
                            }}
                            class={`flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-[13px] transition-colors duration-150 ${
                              sibling.is_current
                                ? "text-accent bg-accent/10 cursor-default"
                                : "text-base-content/60 hover:text-accent cursor-pointer"
                            }`}
                          >
                            {switchingUserId.value === sibling.user_id ? (
                              <span class="loading loading-spinner loading-xs" />
                            ) : (
                              <RestaurantSwitcherImage
                                name={sibling.restaurant_name}
                                url={sibling.image_url}
                              />
                            )}
                            <span class="min-w-0 truncate">
                              {sibling.restaurant_name}
                            </span>
                          </a>
                        </li>
                      ))}
                      <div class="mx-2 my-1 h-px bg-white/6" />
                    </Fragment>
                  )}

                  {/* Language section */}
                  <li class="pointer-events-none px-3 pt-1.5 pb-0.5">
                    <span class="text-[10px] text-base-content/30 uppercase tracking-widest font-semibold p-0 hover:bg-transparent">
                      Language
                    </span>
                  </li>
                  {getAvailableLanguages().map((el) => (
                    <li class="py-0.5" key={el.id}>
                      <a
                        onClick={() => handleSetLocale(el.id)}
                        class={`flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-[13px] transition-colors duration-150 cursor-pointer ${
                          il8n.locale.value === el.id
                            ? "text-accent bg-accent/10"
                            : "text-base-content/60 hover:text-accent "
                        }`}
                      >
                        {loadingLang.value === el.id ? (
                          <span class="loading loading-spinner loading-xs" />
                        ) : (
                          <span class="material-symbols-outlined text-[18px]">
                            {il8n.locale.value === el.id
                              ? "radio_button_checked"
                              : "radio_button_unchecked"}
                          </span>
                        )}
                        {el.name}
                      </a>
                    </li>
                  ))}

                  <div class="mx-2 my-1 h-px bg-white/6" />
                  <div class="">
                    {import.meta.env.VITE_ENV === "staging" && (
                      <>
                        {/* Theme section */}
                        <li class="pointer-events-none px-3 pt-1.5 pb-0.5">
                          <span class="text-[10px] text-base-content/30 uppercase tracking-widest font-semibold p-0 hover:bg-transparent">
                            Theme
                          </span>
                        </li>
                        <ThemeSwitcher />
                      </>
                    )}
                  </div>
                </Fragment>
              )}
              renderFooter={() => (
                <Fragment>
                  <div class="mx-2 my-1 h-px bg-base-content/10" />
                  <li>
                    <a
                      href="#"
                      onClick={handleLogout}
                      class="flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] text-error/70 transition-colors duration-150 hover:bg-error/5 hover:text-error"
                    >
                      <span class="material-symbols-outlined text-[18px]">
                        logout
                      </span>
                      Logout
                    </a>
                  </li>
                  <ReleaseInfo />
                </Fragment>
              )}
            />
          </div>
        </Fragment>
      );

      return (
        <div class="flex items-center justify-between w-full px-0 lg:px-0">
          {content}
        </div>
      );
    };
  },
});

export type QuickLinksProps = {
  mode?: "pos" | "group";
  onRestaurantInfoOpen?: Function;
};

export const QuickLinks = defineComponent<QuickLinksProps>({
  name: "QuickLinks",
  props: ["mode", "onRestaurantInfoOpen"],
  setup(props) {
    return () =>
      props.mode === "group" ? (
        <GroupQuickLinks />
      ) : (
        <PosQuickLinks onRestaurantInfoOpen={props.onRestaurantInfoOpen} />
      );
  },
});
